import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@ludus/ui'
import type { CardPage } from '../../types.ts'
import { formatDate } from '../../utils/format.ts'

interface IdentityPanelProps {
  auditId: string
  card: CardPage['card']
  identity: CardPage['identity']
}

/** The place in the ludus_lead, with the cycle's anomaly flags read-only. */
export function IdentityPanel({ auditId, card, identity }: IdentityPanelProps) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('pages.audits.card.identity.title', { defaultValue: 'Sale' })}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2 text-sm">
        {identity.cycle === null ? (
          <p className="text-xs text-muted">
            {t('pages.audits.card.identity.unlinked', { defaultValue: 'On no cycle yet.' })}
          </p>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted">
                {t('pages.audits.card.identity.origin', { defaultValue: 'Origin' })}
              </dt>
              <dd>{identity.cycle.origin}</dd>
              <dt className="text-muted">
                {t('pages.audits.card.identity.product', { defaultValue: 'Product' })}
              </dt>
              <dd>{identity.cycle.productKey ?? '—'}</dd>
              <dt className="text-muted">
                {t('pages.audits.card.identity.link', { defaultValue: 'Link' })}
              </dt>
              <dd>
                {card.link.confidence ?? '—'} · {card.link.method ?? '—'} ·{' '}
                {card.link.linkedBy ?? '—'}
              </dd>
              <dt className="text-muted">
                {t('pages.audits.card.identity.customer', { defaultValue: 'Customer' })}
              </dt>
              <dd>
                {identity.customer ? `${identity.customer.id} (${identity.customer.anomaly})` : '—'}
              </dd>
            </dl>
            {identity.cycle.anomalyFlags.length > 0 && (
              <ul
                className="flex flex-wrap gap-1"
                aria-label={t('pages.audits.card.identity.flags', {
                  defaultValue: 'Anomaly flags',
                })}
              >
                {identity.cycle.anomalyFlags.map((flag) => (
                  <li key={flag}>
                    <Badge tone="warning" size="sm">
                      {t(`pages.audits.anomalies.kinds.${flag}`, { defaultValue: flag })}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {identity.siblings.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted">
              {t('pages.audits.card.identity.siblings', { defaultValue: 'Also on this sale' })}
            </span>
            <ul className="flex flex-col gap-0.5">
              {identity.siblings.map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-xs">
                  <Link className="hover:underline" to={`/audits/${auditId}/cards/${s.id}`}>
                    {s.name}
                  </Link>
                  <span className="text-muted">
                    {t(`pages.audits.wizard.kind.${s.kind}`, { defaultValue: s.kind })} ·{' '}
                    {formatDate(s.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
